import React from 'react';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8"
        >
          <motion.h1
            className="text-4xl font-serif font-bold text-gray-900 dark:text-white mb-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Privacy Policy
          </motion.h1>

          <motion.div
            className="prose prose-lg dark:prose-invert max-w-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              <em>Last updated: {new Date().toLocaleDateString()}</em>
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                The Super Tip ("we," "our," or "us") respects your privacy and is committed to protecting your personal data.
                This privacy policy will inform you about how we look after your personal data when you visit our website
                and tell you about your privacy rights and how the law protects you.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                2. Information We Collect
              </h2>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Personal Data</h3>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4">
                <li>Name and contact information (email address, phone number)</li>
                <li>Account information (username, password)</li>
                <li>Payment information for tips subscriptions</li>
                <li>Age verification data</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Usage Data</h3>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                <li>Browsing patterns and preferences</li>
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Cookies and tracking technologies</li>
                <li>Sports preferences and tips history</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                3. How We Use Your Information
              </h2>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>To provide and maintain our tips and prognostics services</li>
                <li>To process payments and verify transactions</li>
                <li>To communicate important updates and promotions</li>
                <li>To ensure compliance with legal requirements</li>
                <li>To improve our services and develop new features</li>
                <li>To prevent fraud and ensure platform security</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                4. Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your browsing experience,
                analyze site traffic, and understand where our audience is coming from. You can control
                cookie settings through your browser preferences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                5. Data Sharing and Disclosure
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties
                except in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations and regulatory requirements</li>
                <li>To protect our rights, property, or safety</li>
                <li>With trusted third-party service providers who assist our operations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                6. Age Verification and Responsible Use
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We take age verification seriously. Users must be 18 years or older to access our tips and prognostics.
                We collect and verify age-related information to ensure appropriate access to our content and promote responsible use.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                7. Data Security
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal data
                against unauthorized access, alteration, disclosure, or destruction. However, no method of
                transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                8. International Data Transfers
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of
                residence. These countries may have different data protection laws. We ensure that appropriate
                safeguards are in place to protect your data during such transfers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                9. Your Rights
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You have the following rights regarding your personal data:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>The right to access your personal data</li>
                <li>The right to rectify inaccurate data</li>
                <li>The right to erase your data (subject to legal requirements)</li>
                <li>The right to restrict processing</li>
                <li>The right to data portability</li>
                <li>The right to object to processing</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                10. Contact Us
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or how we handle your personal data,
                please contact us:
              </p>
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Email:</strong> <a href="mailto:thesupertip@gmail.com" className="text-blue-600 dark:text-blue-400 hover:underline">thesupertip@gmail.com</a>
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Subject:</strong> Privacy Policy Inquiry
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                11. Changes to This Policy
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by
                posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
